import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, CheckCircle2, AlertCircle } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const empty = { title: "", slug: "", description: "", category: "Guides", price: 0, published: false, featured: false };

export default function DigitalProducts() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: products } = useQuery({ queryKey: ["admin-products"], queryFn: () => api.get("/admin/downloads/products").then(r => r.data.products) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-products"] }); qc.invalidateQueries({ queryKey: ["products"] }); };
  const del = async (id) => { try { await api.delete(`/admin/downloads/products/${id}`); refresh(); toast.success("Deleted"); } catch (e) { toast.error("Failed"); } };
  const upload = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/downloads/products/${id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } }); refresh(); toast.success("PDF uploaded"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageTitle title="Digital Downloads" subtitle="Sell PDF guides with secure, instant delivery" action={
        <Button className="bg-electric hover:bg-tech gap-1.5" onClick={() => setEditing({ ...empty })} data-testid="new-product-btn"><Plus className="h-4 w-4" /> New Product</Button>
      } />
      <div className="grid md:grid-cols-2 gap-4">
        {(products || []).map((p) => (
          <div key={p.id} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`product-item-${p.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading font-semibold text-navy">{p.title}</h3>
                  <span className="text-sm font-bold text-electric">{money(p.price)}</span>
                  {p.published ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">PUBLISHED</span> : <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">DRAFT</span>}
                </div>
                <p className="text-xs text-slate-500 font-mono mt-0.5">{p.category} · /{p.slug}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(p)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(p.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-600 line-clamp-2">{p.description}</p>
            <div className="mt-3 flex items-center justify-between">
              <span className={`text-xs flex items-center gap-1 ${p.fileKey ? "text-green-600" : "text-amber-600"}`}>
                {p.fileKey ? <><CheckCircle2 className="h-3.5 w-3.5" /> PDF attached ({p.fileSize ? (p.fileSize / 1024).toFixed(0) + " KB" : "ready"})</> : <><AlertCircle className="h-3.5 w-3.5" /> No file — upload to publish</>}
              </span>
              <label className="text-xs text-electric cursor-pointer flex items-center gap-1 hover:underline" data-testid={`upload-product-${p.id}`}>
                <Upload className="h-3.5 w-3.5" /> {p.fileKey ? "Replace PDF" : "Upload PDF"}
                <input type="file" accept="application/pdf" className="hidden" onChange={e => e.target.files[0] && upload(p.id, e.target.files[0])} />
              </label>
            </div>
          </div>
        ))}
      </div>
      {editing && <ProductDialog item={editing} onClose={() => setEditing(null)} onSaved={() => { refresh(); setEditing(null); }} />}
    </div>
  );
}

function ProductDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({ ...empty, ...item });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";
  const submit = async (e) => {
    e.preventDefault();
    const payload = { title: form.title, slug: form.slug, description: form.description, category: form.category, price: Number(form.price), published: form.published, featured: form.featured };
    try { if (item.id) await api.put(`/admin/downloads/products/${item.id}`, payload); else await api.post("/admin/downloads/products", payload); toast.success("Saved"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg"><DialogHeader><DialogTitle>{item.id ? "Edit" : "New"} Product</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="product-form">
        <input className={input} placeholder="Title" required value={form.title} onChange={e => set("title", e.target.value)} data-testid="prod-title" />
        <input className={input} placeholder="Slug (e.g. website-checklist)" required value={form.slug} onChange={e => set("slug", e.target.value)} data-testid="prod-slug" />
        <div className="grid grid-cols-2 gap-3">
          <input className={input} placeholder="Category" value={form.category} onChange={e => set("category", e.target.value)} />
          <input className={input} type="number" step="0.01" placeholder="Price" value={form.price} onChange={e => set("price", e.target.value)} data-testid="prod-price" />
        </div>
        <textarea className={`${input} h-auto py-2`} rows={3} placeholder="Description" value={form.description} onChange={e => set("description", e.target.value)} />
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={e => set("published", e.target.checked)} data-testid="prod-published" /> Published</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={e => set("featured", e.target.checked)} /> Featured</label>
        </div>
        <p className="text-xs text-slate-500">Publish only after uploading the PDF. Products without a file can't be purchased.</p>
        <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="prod-save">Save Product</Button>
      </form>
    </DialogContent></Dialog>
  );
}
